import {
  Box,
  Heading,
  Text,
  Code,
  Link,
  UnorderedList,
  OrderedList,
  ListItem,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import remarkGfmNoAutolink from "./remarkGfmNoAutolink.js";

const MarkdownRenderer = ({ children, ...props }) => {
  const components = {
    h2: ({ children, ...props }) => (
      <Heading
        as="h2"
        size="sm"
        mb={4}
        mt={12}
        pt={6}
        borderTopWidth="1px"
        borderColor="border"
        {...props}
      >
        {children}
      </Heading>
    ),
    h3: ({ children, ...props }) => (
      <Heading as="h3" size="xs" mb={3} mt={8} {...props}>
        {children}
      </Heading>
    ),

    // Paragraphs and text
    p: ({ children, ...props }) => (
      <Text mb={4} lineHeight="1.75" {...props}>
        {children}
      </Text>
    ),

    // Links
    a: ({ href, children, ...props }) => (
      <Link
        href={href}
        color="accent"
        textDecoration="underline"
        textDecorationColor="border"
        textUnderlineOffset="2px"
        _hover={{ textDecorationColor: "accent" }}
        {...props}
      >
        {children}
      </Link>
    ),

    // Code
    code: ({ inline, children, className, ...props }) => {
      if (inline) {
        return (
          <Code colorScheme="gray" fontSize="sm" px={1} py={0.5} {...props}>
            {children}
          </Code>
        );
      }

      return (
        <Box
          as="pre"
          bg="bg-muted"
          p={4}
          rounded="md"
          mb={4}
          overflowX="auto"
          fontSize="sm"
          {...props}
        >
          <Code
            as="code"
            className={className}
            bg="transparent"
            p={0}
            fontSize="inherit"
          >
            {children}
          </Code>
        </Box>
      );
    },

    // Lists
    ul: ({ children, ...props }) => (
      <UnorderedList mb={4} pl={4} {...props}>
        {children}
      </UnorderedList>
    ),
    ol: ({ children, ...props }) => (
      <OrderedList mb={4} pl={4} {...props}>
        {children}
      </OrderedList>
    ),
    li: ({ children, ...props }) => (
      <ListItem mb={1} {...props}>
        {children}
      </ListItem>
    ),

    // Horizontal rule
    hr: ({ ...props }) => <Divider my={8} {...props} />,

    // Images
    img: ({ src, alt, ...props }) => (
      <Box
        as="img"
        src={src}
        alt={alt}
        w="full"
        rounded="md"
        mb={4}
        {...props}
      />
    ),

    // Tables
    table: ({ children, ...props }) => (
      <Box overflowX="auto" mb={6}>
        <Table size="sm" variant="simple" {...props}>
          {children}
        </Table>
      </Box>
    ),
    thead: ({ children, ...props }) => <Thead {...props}>{children}</Thead>,
    tbody: ({ children, ...props }) => <Tbody {...props}>{children}</Tbody>,
    tr: ({ children, ...props }) => <Tr {...props}>{children}</Tr>,
    th: ({ children, ...props }) => (
      <Th fontSize="xs" whiteSpace="normal" {...props}>
        {children}
      </Th>
    ),
    td: ({ children, ...props }) => (
      <Td fontSize="sm" whiteSpace="normal" {...props}>
        {children}
      </Td>
    ),

    // Blockquote (used for callouts and mid-article CTAs)
    blockquote: ({ children, ...props }) => (
      <Box
        as="blockquote"
        borderLeft="4px"
        borderColor="accent"
        pl={5}
        pr={5}
        py={4}
        my={8}
        bg="bg-subtle"
        rounded="md"
        sx={{ "& > p:last-child": { mb: 0 } }}
        {...props}
      >
        {children}
      </Box>
    ),
  };

  return (
    <Box {...props}>
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfmNoAutolink]}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
};

export { MarkdownRenderer };
